import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectPrivileges } from 'store/privileges/slice'
import { getPrivileges } from 'store/privileges/thunks'
import { selectCountries } from 'store/countries/slice'
import { getCountries } from 'store/countries/thunks'
import { selectLocale } from 'store/locale/slice'
import { searchPrivilegeUsers } from 'store/users/thunks'
import { useAppDispatch } from 'store'
import { sendErrorNotification } from 'utils/notifications'
import { Link } from 'react-router-dom'
import { ClientRoute } from 'routes/client'
import { getCurrentUTCDateTime, localizedDateTime } from 'utils/formatDate'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { selectUsers } from 'store/users/slice'
import ClearIcon from '@mui/icons-material/Clear'
import SubmitButton from 'components/form/SubmitButton'
import { CheckBoxOutlineBlankOutlined } from '@mui/icons-material'

export default function Privileges(): JSX.Element {
  const intl = useIntl()
  const { privileges } = useSelector(selectPrivileges)
  const { countries } = useSelector(selectCountries)
  const locale = useSelector(selectLocale)
  const dispatch = useAppDispatch()
  const [privilege, setPrivilege] = useState<string | ''>('')
  const [name, setName] = useState<string | ''>('')
  const [email, setEmail] = useState<string | ''>('')
  const [selectedTiers, setSelectedTiers] = useState<string[]>([])
  const [selectedCountries, setSelectedCountries] = useState<number[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const { availableLanguages, language } = useSelector(selectLocale)
  const [privilegeUsers, setPrivilegeUsers] = useState<PrivilegedUsers[]>([])
  const [searchLimit, setSearchLimit] = useState<number>(50)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const { status } = useSelector(selectUsers)
  const isLoading = status === 'loading' ? true : false
  const [expiredOnly, setExpiredOnly] = useState<boolean>(false)

  useEffect(() => {
    if (privileges.length === 0) {
      dispatch(getPrivileges())
    }
  }, [dispatch, privileges])

  useEffect(() => {
    if (countries.length === 0) {
      dispatch(getCountries())
    }
  }, [dispatch, countries])

  const handlePrivilegeChange = (event: SelectChangeEvent) => {
    setPrivilege(event.target.value)
  }

  const userColumns: GridColDef<PrivilegedUsers>[] = [
    {
      headerName: intl.formatMessage({ id: 'strings.name' }),
      field: 'userName',
      cellClassName: 'ellipsis-cell',
      flex: 1,
      valueGetter: (_, row) => row.userName,
      renderCell: (params) => (
        <Link
          style={{
            textDecoration: 'none',
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
          to={ClientRoute.ADMIN_USER(params.row.userId || 0)}
        >
          <Typography sx={{ color: 'green', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.userName}
          </Typography>
        </Link>
      ),
    },
    {
      field: 'email',
      cellClassName: 'ellipsis-cell',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.email' }),
      renderCell: (params) => (
        <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.row.email}
        </Typography>
      ),
    },
    {
      field: 'privilege',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.privilege' }),
      renderCell: (params) => toChip(params.row.privilege),
    },
    {
      field: 'status',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.status' }),
      valueGetter: (_, row) => isActive(row.validFrom?.date, row.validTo?.date),
      renderCell: (params) => (
        <span
          style={{
            color: params.value ? 'green' : 'red',
          }}
        >
          {params.value ? 'Active' : 'Expired'}
        </span>
      ),
      sortComparator: (a, b) => Number(b) - Number(a),
    },
    {
      field: 'validFrom',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.validFrom' }),
      description: 'UTC+0',
      valueGetter: (_, row) => (row.validFrom?.date ? new Date(row.validFrom.date).getTime() : null),
      renderCell: (params) =>
        params.row.validFrom ? localizedDateTime(new Date(params.row.validFrom?.date), locale.language.code) : '-',
      sortable: true,
    },
    {
      field: 'validTo',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.validTo' }),
      description: 'UTC+0',
      valueGetter: (_, row) => (row.validTo?.date ? new Date(row.validTo.date).getTime() : null),
      renderCell: (params) =>
        params.row.validTo ? localizedDateTime(new Date(params.row.validTo?.date), locale.language.code) : '-',
      sortable: true,
    },
    {
      field: 'notes',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.notes' }),
    },
    {
      field: 'countries',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.countries' }),
      renderCell: (params) => {
        if (!params.row.targetCountries) {
          return '-'
        }
        const chip = countries
          .filter((c) => params.row.targetCountries && params.row.targetCountries.includes(c.id))
          .map((c) => c.name)
        return (
          <>
            {chip.slice(0, 2).map((name, i) => (
              <TableChips key={name + i} label={name} />
            ))}
            {chip.length > 2 && <span style={{ marginTop: 10 }}>...</span>}
          </>
        )
      },
    },
    {
      field: 'languages',
      flex: 1,
      headerName: intl.formatMessage({ id: 'strings.languages' }),
      renderCell: (params) => {
        if (!params.row.targetLanguages) {
          return '-'
        }
        const chips = availableLanguages
          .filter(
            (l) => params.row.targetLanguages && l.code && params.row.targetLanguages.includes(l.code?.toLowerCase()),
          )
          .map((l) => l.code)
        const displayNames = new Intl.DisplayNames([language.code], { type: 'language' })
        return (
          <>
            {chips.slice(0, 2).map((name, i) => (
              <TableChips key={name + i} label={displayNames.of(name)} />
            ))}
            {chips.length > 2 && <span style={{ marginTop: 10 }}>...</span>}
          </>
        )
      },
    },
  ]

  const handlePaginationModelChange = (model) => {
    if (isLoading) {
      return
    }
    setPage(model.page)
    setSearchLimit(model.pageSize)
    searchPrivilegedUsers(undefined, model.page, model.pageSize)
  }

  const searchPrivilegedUsers = async (
    event?: React.FormEvent<HTMLFormElement>,
    updatedPage?: number,
    newSearchLimit?: number,
  ) => {
    event?.preventDefault()
    const offset = updatedPage ? updatedPage * searchLimit : 0

    try {
      const res = await dispatch(
        searchPrivilegeUsers({
          name: name,
          email: email,
          privilege: selectedTiers?.join(','),
          targetLanguages: selectedLanguages?.join(','),
          targetCountries: selectedCountries?.join(','),
          limit: newSearchLimit ? newSearchLimit : searchLimit,
          offset: offset,
          activeOnly: Number(true),
          expiredOnly: Number(expiredOnly),
        }),
      ).unwrap()

      setPrivilegeUsers(
        res.users.sort((a, b) => {
          const aActive = isActive(a.validFrom?.date, a.validTo?.date)
          const bActive = isActive(b.validFrom?.date, b.validTo?.date)
          if (aActive !== bActive) {
            return aActive ? -1 : 1
          }
          if (a.userName && b.userName) {
            return a.userName.localeCompare(b.userName)
          }
          return 0
        }),
      )
      setTotalCount(res.total)
    } catch (error) {
      console.error('Error copying tee boxes:', error)
      sendErrorNotification(`no users found.`)
    }
  }

  const isActive = (validFrom: Date | null, validTo: Date | null) => {
    const from = validFrom ? new Date(validFrom) : new Date()
    const to = validTo ? new Date(validTo) : new Date('2100-01-01')
    const now = new Date(getCurrentUTCDateTime())

    return now >= from && now <= to
  }

  const toChip = (value: string | number[] | string[] | undefined) => {
    const chippedValue = privileges.find((p) => p.name === value)

    if (!chippedValue?.metadata) {
      return <TableChips label={'Admin'} />
    }
    return <TableChips label={chippedValue?.metadata?.groupName} />
  }

  const handleTierChange = (event: React.MouseEvent<HTMLElement>, newTiers: string[]) => {
    setSelectedTiers(newTiers)
  }

  const getPrivilegeOptions = () => {
    const filteredPrivileges = privileges.filter((privilege) => privilege.name !== 'GOLD_MEMBERSHIP')

    const tierPrivileges = filteredPrivileges.filter((privilege) => privilege.metadata?.groupName.startsWith('Tier'))

    const menuItems: React.ReactNode[] = []

    if (tierPrivileges.length > 0) {
      menuItems.push(
        <MenuItem key="TIER_GROUP" value="TIER">
          Tier
        </MenuItem>,
      )
    }

    return menuItems
  }

  const getLanguageOptions = () => {
    const languages = availableLanguages.filter((availableLanguages) => availableLanguages.code)
    const languageOptions = languages.map((languages: Language) => {
      return { key: languages.code, label: languages.code }
    })

    return languageOptions
  }

  const getCountryOptions = () => {
    const country = countries.filter((countries) => countries.name)
    const countriesOptions = country.map((country: Country) => {
      return { key: country.id, label: country.name }
    })

    return countriesOptions
  }

  const getTiers = () => {
    return privileges.filter((privilege) => privilege.metadata?.groupName.startsWith('Tier'))
  }

  const resetFilters = () => {
    setEmail('')
    setName('')
    setSelectedCountries([])
    setSelectedLanguages([])
    setSelectedTiers([])
    setPrivilegeUsers([])
    setExpiredOnly(false)
  }

  return (
    <PageContainer>
      <form onSubmit={searchPrivilegedUsers}>
        <Stack gap={'48px'} paddingBottom={'48px'}>
          <FormControl fullWidth size="small" required sx={{ width: '30%', gap: '48px' }}>
            <InputLabel variant="outlined" id="privilege-label">
              {intl.formatMessage({ id: 'strings.privileges' })}
            </InputLabel>

            <Select
              label="Privilege"
              labelId="privilege-label"
              onChange={handlePrivilegeChange}
              value={privilege}
              sx={{ borderRadius: '10px' }}
            >
              {getPrivilegeOptions()}
            </Select>
          </FormControl>
          <FormControl sx={{ gap: '24px' }} fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'space-between',
                width: '80%',
              }}
            >
              <Typography variant="h6" component="h2" sx={{ color: '#1E2939', display: 'flex', alignItems: 'center' }}>
                Filters
              </Typography>
              <Button sx={{ color: '#1E2939' }} onClick={resetFilters} startIcon={<ClearIcon />}>
                clear all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: '24px' }}>
              <TextField
                size="small"
                sx={{
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
                rows={2}
                label="Name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                size="small"
                sx={{
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
                label="Email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FormGroup sx={{ justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={expiredOnly}
                      onChange={() => setExpiredOnly(!expiredOnly)}
                      icon={<CheckBoxOutlineBlankOutlined />}
                    />
                  }
                  label="Show also expired"
                />
              </FormGroup>
            </Box>
            {privilege === 'TIER' && (
              <FormControl>
                <InputLabel>Tier(s)</InputLabel>
                <ToggleButtonGroup size="small" sx={{ marginTop: 5 }} value={selectedTiers} onChange={handleTierChange}>
                  <Grid container spacing={-1} sx={{ gap: 1 }}>
                    {privileges &&
                      getTiers().map((tier) => (
                        <TierButton key={tier.id} value={tier.name}>
                          <Typography
                            sx={{
                              textTransform: 'none',
                            }}
                          >
                            {tier.metadata?.groupName}
                          </Typography>
                        </TierButton>
                      ))}
                  </Grid>
                </ToggleButtonGroup>
              </FormControl>
            )}
          </FormControl>

          {selectedTiers.some((tier) => tier !== 'FEATURED_STARS_TIER_1') && (
            <FormControl fullWidth>
              <Stack display={'flex'} flexDirection={'row'} gap={2}>
                <Autocomplete
                  multiple
                  options={getCountryOptions()}
                  getOptionLabel={(option) => option.label}
                  sx={{
                    width: '30%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  onChange={(e, newCountry) => setSelectedCountries(newCountry.map((o) => o.key))}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <MenuItem {...props}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={(params) => <TextField {...params} label={'Countries (Tiers 2-4)'} />}
                />
                <Autocomplete
                  multiple
                  options={getLanguageOptions()}
                  getOptionLabel={(option) => option.label}
                  sx={{
                    width: '30%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                  disableCloseOnSelect
                  onChange={(e, newLang) => setSelectedLanguages(newLang.map((o) => o.label))}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  renderOption={(props, option, { selected }) => (
                    <MenuItem {...props}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={(params) => <TextField {...params} label={'Languages (Tiers 2-4)'} />}
                />
              </Stack>
            </FormControl>
          )}
          <SearchButton
            status={privilegeUsers.length === 0 ? status : 'idle'}
            text={intl.formatMessage({ id: 'strings.search' })}
          />
        </Stack>
      </form>

      <SearchResults>
        <Typography
          sx={{ paddingBottom: '16px', fontSize: '16px', fontWeight: '400', fontStyle: 'normal' }}
        >{`Search results (${totalCount})`}</Typography>
        <DataGrid
          sx={{
            width: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              height: 51,
              minHeight: 51,
              maxHeight: 51,
              backgroundColor: '#098F50',
              borderRadius: '10px 10px 0 0',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#098F50',
              color: '#fff',
              fontWeight: 'bold',
              minHeight: 51,
              maxHeight: 51,
              height: 51,
              display: 'flex',
              alignItems: 'center',
              lineHeight: '51px',
            },
            '& .ellipsis-cell .MuiDataGrid-cellContent': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-actions button': {
              color: 'black',
            },
            '& .MuiTablePagination-actions button .MuiSvgIcon-root': {
              color: 'black',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
            '& .MuiTablePagination-root .MuiSelect-icon': {
              color: 'black',
            },
          }}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page: page, pageSize: searchLimit }}
          pageSizeOptions={[50, 100]}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(row) => row.id}
          rows={privilegeUsers}
          columns={userColumns}
          disableColumnMenu
          autoHeight
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </SearchResults>
    </PageContainer>
  )
}
const PageContainer = styled.div`
  margin: 10px;
  padding: 10px 20px 20px;
  min-height: 500px;
  background-color: #fff;
  width: 100%;
`

const TierButton = styled(ToggleButton)`
  display: flex;
  width: 85px;
  height: 42px;
  padding: 8.5px 20.219px 9.5px 21px;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: 10px;

  &.Mui-selected {
    background-color: #098f50;
    color: #fff;
  }

  &.Mui-selected:hover {
    background-color: #087a45;
  }
`
const SearchResults = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  margin-bottom: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const TableChips = styled(Chip)`
  color: #373d40;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  margin: 0px 4px 0px 0px;
`
const SearchButton = styled(SubmitButton)`
  width: 5%;
  background: #098f50;
  color: #ffffff;
  variant: contained;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;

  &:hover {
    background: #087a45;
  }
`
